/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * incentiveService :: incentive programs, goals, scores + lifecycle transitions
 * over RLS (Supabase Auth). Like the other ported services we talk to the tables
 * DIRECTLY with the authenticated client; RLS (migration 0041) pins every row to
 * the caller's account via is_account_member(account_id), and DB triggers gate
 * the state machine. This layer adds: field whitelists on writes, the transition
 * verbs (one per legal edge), and generic client-facing error messages.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { computePayout } from "./payout.js";
import { hasAutomaticSource } from "./metricResolver.js";
import { STATUS, DATA_SOURCE } from "./constants.js";

// ── Active account ───────────────────────────────────────────────────────────
let _accountIdPromise = null;
export function resetIncentiveAccount() { _accountIdPromise = null; }

async function activeAccountId() {
  if (_accountIdPromise) return _accountIdPromise;
  _accountIdPromise = (async () => {
    const acct = await getMyPrimaryAccount();
    if (!acct?.accountId) throw new Error("No workspace found for your account.");
    return acct.accountId;
  })();
  return _accountIdPromise;
}

// Translate a Postgres/RLS error into a safe, specific client message. The real
// cause is always console.error'd by the caller; we never leak schema/FK names.
function liftError(error, fallback) {
  const code = error?.code;
  const msg = error?.message || "";
  if (code === "23514" || /Illegal incentive status transition/i.test(msg))
    return new Error("That status change isn't allowed from the program's current state.");
  if (code === "42501" || /row-level security|Only a manager/i.test(msg))
    return new Error("You don't have permission to do that.");
  if (/Goals can only be edited while the program is in draft/i.test(msg))
    return new Error("Goals can only be changed while the program is a draft.");
  if (/Scores can only be captured while scoring/i.test(msg))
    return new Error("Scores can only be entered once the program is in scoring.");
  if (/immutable/i.test(msg))
    return new Error("This program is completed — its scores are locked.");
  return new Error(fallback);
}

// ── Per-account settings (require_review) ────────────────────────────────────
// Read-only here: the WRITE side lives in System Settings → Incentives (it's a
// company-admin policy, hidden from reps). This read decides which draft exit to
// take (submitDraft → pending_review vs. sent).
export async function getRequireReview() {
  return (await getReviewConfig()).requireReview;
}

/**
 * Review policy + who reviews. The Sales Manager (set in System Settings →
 * Incentives) is the account-level user who receives a program for review when
 * review is required; the DB (incentive_can_review) lets them — or an
 * owner/admin — approve/reject.
 *
 * THROWS on a read error (fail-closed): submit must not treat a transient
 * failure as "no review required" and silently send a program past its reviewer,
 * and the review UI must not treat it as "no reviewer".
 */
export async function getReviewConfig() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("incentive_settings")
    .select("require_review, sales_manager_user_id")
    .eq("account_id", acct)
    .maybeSingle();
  if (error) {
    console.error("[incentiveService.getReviewConfig]", error);
    throw new Error("Couldn't check the review policy. Please try again.");
  }
  return { requireReview: data?.require_review === true, salesManagerUserId: data?.sales_manager_user_id ?? null };
}

// ── Programs ─────────────────────────────────────────────────────────────────
const PROGRAM_WRITE_FIELDS = new Set([
  "name", "distributor_name", "brand_manager_name", "brand_manager_email",
  "start_date", "end_date", "notes", "target_states",
]);

function pickProgramFields(patch) {
  const out = {};
  for (const k of PROGRAM_WRITE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) {
      let v = patch[k];
      if (k === "target_states") {
        // Array of state names; sanitize to trimmed, de-duped strings.
        const arr = Array.isArray(v) ? v : [];
        out[k] = [...new Set(arr.map((s) => String(s).trim()).filter(Boolean))];
        continue;
      }
      if (typeof v === "string") v = v.trim();
      if (v === "") v = null;
      out[k] = v;
    }
  }
  if (Object.prototype.hasOwnProperty.call(out, "name")) {
    const n = String(out.name || "").trim();
    if (n.length < 1 || n.length > 160) throw new Error("Program name must be 1–160 characters.");
    out.name = n;
  }
  return out;
}

/** List programs (newest first) with a goal count. Optional status filter. */
export async function listPrograms({ status } = {}) {
  const acct = await activeAccountId();
  let q = supabase
    .from("incentive_programs")
    .select("id, name, distributor_name, target_states, brand_manager_name, brand_manager_email, start_date, end_date, status, sent_at, sent_to, send_status, send_error, created_at, updated_at, goals:incentive_goals(count)")
    .eq("account_id", acct)
    .order("created_at", { ascending: false });
  if (status) q = q.eq("status", status);
  const { data, error } = await q;
  if (error) { console.error("[incentiveService.listPrograms]", error); throw new Error("Could not load incentive programs."); }
  return (data ?? []).map((p) => ({ ...p, goal_count: p.goals?.[0]?.count ?? 0 }));
}

/** Full program with its goals, scores, and attachments. */
export async function getProgram(id) {
  const acct = await activeAccountId();
  const [progRes, goalsRes, scoresRes, attRes] = await Promise.all([
    supabase.from("incentive_programs").select("*").eq("account_id", acct).eq("id", id).maybeSingle(),
    supabase.from("incentive_goals").select("*").eq("account_id", acct).eq("program_id", id).order("sort_order", { ascending: true }),
    supabase.from("incentive_scores").select("*").eq("account_id", acct).eq("program_id", id),
    supabase.from("incentive_attachments").select("*").eq("account_id", acct).eq("program_id", id).order("created_at", { ascending: true }),
  ]);
  for (const r of [progRes, goalsRes, scoresRes, attRes]) {
    if (r.error) { console.error("[incentiveService.getProgram]", r.error); throw new Error("Could not load the program."); }
  }
  if (!progRes.data) throw new Error("Program not found.");
  return {
    program: progRes.data,
    goals: goalsRes.data ?? [],
    scores: scoresRes.data ?? [],
    attachments: attRes.data ?? [],
  };
}

/** Create a new draft program. Status is server-defaulted to draft; created_by
 *  is the JWT user (DB default auth.uid()) — never accepted from the client. */
export async function createProgram(input) {
  const acct = await activeAccountId();
  const fields = pickProgramFields(input || {});
  if (!fields.name) throw new Error("Give the program a name.");
  const { data, error } = await supabase
    .from("incentive_programs")
    .insert({ account_id: acct, ...fields })
    .select("id")
    .single();
  if (error) { console.error("[incentiveService.createProgram]", error); throw liftError(error, "Could not create the program."); }
  return data.id;
}

/** Update a draft program's metadata (whitelisted fields only). */
export async function updateProgram(id, patch) {
  const acct = await activeAccountId();
  const fields = pickProgramFields(patch || {});
  if (Object.keys(fields).length === 0) return;
  const { error } = await supabase
    .from("incentive_programs").update(fields).eq("account_id", acct).eq("id", id);
  if (error) { console.error("[incentiveService.updateProgram]", error); throw liftError(error, "Could not save the program."); }
}

export async function deleteProgram(id) {
  const acct = await activeAccountId();
  const { error } = await supabase
    .from("incentive_programs").delete().eq("account_id", acct).eq("id", id);
  if (error) { console.error("[incentiveService.deleteProgram]", error); throw liftError(error, "Could not delete the program."); }
}

// ── Goals ────────────────────────────────────────────────────────────────────
const GOAL_WRITE_FIELDS = new Set([
  "metric_type", "description", "target_value", "unit_label",
  "payout_type", "payout_amount", "payout_tiers",
  "target_accounts", "target_products", "sort_order", "custom_text", "payout_custom",
]);

// Targeting snapshots (item 233). We store a copy of the account/SKU rather than
// an FK: an incentive program is an agreement with a distributor and has to keep
// reading correctly after a SKU is renamed or a door drops out of the reference
// data. Only these keys survive — anything else the picker happened to carry is
// dropped, so a component can never smuggle a column into the row.
const ACCOUNT_KEYS = ["id", "name", "chain", "city", "state"];
const PRODUCT_KEYS = ["id", "name"];

function pickSnapshots(list, keys, max = 200) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const src = item && typeof item === "object" ? item : { id: item, name: item };
    const id = src.id == null ? null : String(src.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const row = {};
    for (const k of keys) {
      const v = src[k];
      row[k] = v == null ? "" : String(v).slice(0, 200);
    }
    row.id = id;
    out.push(row);
    if (out.length >= max) break;
  }
  return out;
}

function pickGoalFields(patch) {
  const out = {};
  for (const k of GOAL_WRITE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) out[k] = patch[k];
  }
  // Free-text custom fields: trim, blank → null.
  for (const k of ["custom_text", "payout_custom"]) {
    if (k in out) { const t = String(out[k] ?? "").trim(); out[k] = t || null; }
  }
  // Coerce numerics; default empty payout structure.
  if ("target_value" in out) out.target_value = Number(out.target_value) || 0;
  if ("payout_amount" in out) out.payout_amount = out.payout_amount === "" || out.payout_amount == null ? null : Number(out.payout_amount);
  if ("payout_tiers" in out && !Array.isArray(out.payout_tiers)) out.payout_tiers = [];
  if ("target_accounts" in out) out.target_accounts = pickSnapshots(out.target_accounts, ACCOUNT_KEYS);
  if ("target_products" in out) out.target_products = pickSnapshots(out.target_products, PRODUCT_KEYS);

  // data_source is DERIVED, never accepted from the body (it isn't in the
  // whitelist at all). It records whether this goal's actual can be pulled from
  // depletion reports — the resolver owns that answer, so it is asked here
  // rather than trusted from a component. Item 231: it used to be hard-coded to
  // 'manual_upload' on every write, which is what made the app's own scoring
  // screen contradict the module's headline promise.
  if ("metric_type" in out) {
    out.data_source = hasAutomaticSource({
      metric_type: out.metric_type,
      target_products: out.target_products ?? patch.target_products,
    }) ? DATA_SOURCE.SYSTEM : DATA_SOURCE.MANUAL;
  }
  return out;
}

/** Insert or update one goal. The DB trigger blocks this unless the program is
 *  a draft. Pass an existing { id } to update; omit it to insert. */
export async function saveGoal(programId, goal) {
  const acct = await activeAccountId();
  const fields = pickGoalFields(goal || {});
  if (!fields.metric_type) throw new Error("Pick a metric type for the goal.");
  if (goal?.id) {
    const { error } = await supabase
      .from("incentive_goals").update(fields).eq("account_id", acct).eq("id", goal.id);
    if (error) { console.error("[incentiveService.saveGoal.update]", error); throw liftError(error, "Could not save the goal."); }
    return goal.id;
  }
  const { data, error } = await supabase
    .from("incentive_goals")
    .insert({ account_id: acct, program_id: programId, ...fields })
    .select("id").single();
  if (error) { console.error("[incentiveService.saveGoal.insert]", error); throw liftError(error, "Could not add the goal."); }
  return data.id;
}

export async function deleteGoal(goalId) {
  const acct = await activeAccountId();
  const { error } = await supabase
    .from("incentive_goals").delete().eq("account_id", acct).eq("id", goalId);
  if (error) { console.error("[incentiveService.deleteGoal]", error); throw liftError(error, "Could not delete the goal."); }
}

// ── Lifecycle transitions (one verb per legal edge) ──────────────────────────
// Each verb updates status (+ any edge-specific fields). The DB trigger is the
// real gate; these map failures to friendly messages.
async function transition(id, fields, fallback) {
  const acct = await activeAccountId();
  const { error } = await supabase
    .from("incentive_programs").update(fields).eq("account_id", acct).eq("id", id);
  if (error) { console.error("[incentiveService.transition]", fields.status, error); throw liftError(error, fallback); }
}

/** draft → pending_review (when the company requires review). */
export function submitForReview(id) {
  return transition(id, { status: STATUS.PENDING_REVIEW }, "Could not submit for review.");
}

/** pending_review → approved (manager only — enforced by the DB). */
async function approveProgram(id, note) {
  const { data: { user } } = await supabase.auth.getUser();
  return transition(id, {
    status: STATUS.APPROVED,
    reviewed_by: user?.id ?? null,
    reviewed_at: new Date().toISOString(),
    review_notes: note ? String(note).slice(0, 2000) : null,
  }, "Could not approve the program.");
}

/** pending_review → draft (rejection; manager only). */
async function rejectProgram(id, note) {
  const { data: { user } } = await supabase.auth.getUser();
  return transition(id, {
    status: STATUS.DRAFT,
    reviewed_by: user?.id ?? null,
    reviewed_at: new Date().toISOString(),
    review_notes: note ? String(note).slice(0, 2000) : null,
  }, "Could not reject the program.");
}

export { approveProgram, rejectProgram };

/**
 * approved → sent  OR  draft → sent (when review isn't required). Stamps sent_at
 * and then actually SENDS it (item 230) — see sendProgram below. Returns the
 * delivery result so the caller can tell the user what really happened; the
 * status transition itself never fails because of email.
 */
export async function markSent(id) {
  await transition(id, { status: STATUS.SENT, sent_at: new Date().toISOString() }, "Could not mark the program as sent.");
  return sendProgram(id);
}

// ── Delivery to the distributor (item 230) ───────────────────────────────────
// "Send it to the distributors" is the middle verb of this module's headline and
// for one release it wrote a status column and stopped: brand_manager_email was
// collected, whitelisted, displayed and copied — and never read by anything that
// sends. This is the same shape pos-fulfillment uses for its shipped mail
// (posService.notifyShipped → pos-shipped-notify → send-email):
//
//   the browser sends ONLY the program id. The edge function re-reads the row,
//   derives the recipient from brand_manager_email, renders the packet, hands
//   off to send-email, and writes the delivery record back with the service
//   role. send-email's internal secret never reaches this bundle, and a caller
//   cannot aim the mail at an arbitrary address.
//
// A failure is reported, never swallowed — backlog item 204's lesson: "the
// distributor was told" and "nothing left the building" must not look identical.
const SEND_REASONS = {
  no_program:      "the program could not be identified",
  no_recipient:    "there is no brand manager email on the program",
  not_sent:        "the program is not marked Sent yet",
  invoke_failed:   "the notification service could not be reached",
  no_response:     "the notification service returned nothing",
  email_not_configured: "email is not configured on the server",
  send_failed:     "the email provider rejected it — check Admin → Email Log",
  error:           "the notification service hit an error",
};

/** Just the cause clause for a send failure ("the notification service …"). */
export function sendReason(reason) {
  return SEND_REASONS[reason] ?? "an unknown problem";
}

/** Human-readable warning for a send result, or null when it really sent. */
export function sendWarning(result) {
  if (result?.sent) return null;
  return `Nothing was emailed to the distributor — ${sendReason(result?.reason)}.`;
}

/** Confirmation line for a delivered program, or null. */
export function sendConfirmation(program) {
  if (program?.send_status !== "sent" || !program?.sent_to) return null;
  const when = program.sent_at
    ? new Date(program.sent_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;
  return `Emailed to ${program.sent_to}${when ? ` on ${when}` : ""}.`;
}

/**
 * Record a FAILED delivery attempt on the program so the failure survives a page
 * reload. Only the failure direction is writable from a browser — migration 0095
 * has a trigger that rejects a client-side `send_status = 'sent'`, because that
 * value is a claim that mail actually left and only the service role may make it.
 */
async function recordSendFailure(id, reason) {
  const acct = await activeAccountId();
  const { error } = await supabase
    .from("incentive_programs")
    .update({
      send_status: "failed",
      send_error: String(reason || "error").slice(0, 200),
      send_attempted_at: new Date().toISOString(),
    })
    .eq("account_id", acct).eq("id", id);
  // Best-effort: the send result is still returned to the caller and shown.
  if (error) console.error("[incentiveService.recordSendFailure]", error);
}

/**
 * Email the program packet to the distributor's brand manager.
 * @returns {Promise<{sent:boolean, reason?:string, to?:string}>}
 */
export async function sendProgram(id) {
  if (!id) return { sent: false, reason: "no_program" };
  let result;
  try {
    const { data, error } = await supabase.functions.invoke("incentive-notify", {
      body: { programId: id },
    });
    if (error) {
      console.error("[incentiveService.sendProgram]", error);
      result = { sent: false, reason: "invoke_failed" };
    } else {
      result = data ?? { sent: false, reason: "no_response" };
    }
  } catch (e) {
    console.error("[incentiveService.sendProgram]", e);
    result = { sent: false, reason: "invoke_failed" };
  }
  if (!result.sent) await recordSendFailure(id, result.reason);
  return result;
}

/** Re-send a program that is already past draft (the Resend button). */
export function resendProgram(id) {
  return sendProgram(id);
}

/** sent → active. */
export function markActive(id) {
  return transition(id, { status: STATUS.ACTIVE }, "Could not activate the program.");
}

/** active → scoring. */
export function beginScoring(id) {
  return transition(id, { status: STATUS.SCORING }, "Could not start scoring.");
}

/** scoring → completed. */
export function completeProgram(id) {
  return transition(id, { status: STATUS.COMPLETED }, "Could not complete the program.");
}

/**
 * Goal-completeness gate enforced ONLY when a program leaves draft (not during
 * autosave, which persists partial drafts). Blocks sending a goal that can't be
 * scored or that would over/under-pay: a non-custom goal needs a positive
 * target; a flat/per-unit payout needs a positive amount; tiered needs a tier;
 * custom needs its free text.
 */
function assertGoalsComplete(goals) {
  if (!goals || goals.length === 0) throw new Error("Add at least one goal before submitting.");
  goals.forEach((g, i) => {
    const label = `Goal ${i + 1}`;
    if (g.metric_type === "custom") {
      if (!String(g.custom_text || "").trim()) throw new Error(`${label}: add the goal details.`);
    } else if (!(Number(g.target_value) > 0)) {
      throw new Error(`${label}: enter a target greater than 0.`);
    }
    if (g.payout_type === "custom") {
      if (!String(g.payout_custom || "").trim()) throw new Error(`${label}: describe the payout.`);
    } else if (g.payout_type === "tiered") {
      const tiers = Array.isArray(g.payout_tiers) ? g.payout_tiers : [];
      if (!tiers.some((t) => Number(t.amount) > 0)) throw new Error(`${label}: add at least one payout tier with an amount.`);
    } else if (!(Number(g.payout_amount) > 0)) {
      throw new Error(`${label}: enter a payout amount greater than 0.`);
    }
  });
}

/**
 * Submit a draft. Validates goal completeness first, then: if the company
 * requires review → pending_review; otherwise the draft → sent edge is taken
 * directly AND the packet is emailed to the brand manager. The review-policy
 * read is fail-closed.
 * @returns {Promise<{status:string, delivery:object|null}>}
 */
export async function submitDraft(id) {
  const { goals } = await getProgram(id);
  assertGoalsComplete(goals);
  const requireReview = await getRequireReview();
  if (requireReview) { await submitForReview(id); return { status: STATUS.PENDING_REVIEW, delivery: null }; }
  const delivery = await markSent(id);
  return { status: STATUS.SENT, delivery };
}

// ── Scoring (manual actuals → immutable incentive_scores) ────────────────────
/**
 * Capture/replace the actual for one goal. The payout is computed here and
 * stored, so the record stays fixed even if the goal's structure changes later.
 * Allowed only while the program is in scoring/active (DB trigger enforces).
 */
export async function saveScore(programId, goal, { actual, notes, payout, resolvedSource } = {}) {
  const acct = await activeAccountId();
  const actualValue = Number(actual) || 0;
  // Custom payouts can't be computed — take the manually-entered amount. For
  // every other type we compute from the goal's structure.
  const computed = computePayout(goal, actualValue);
  let payoutEarned;
  if (computed == null) {
    payoutEarned = (payout === "" || payout == null) ? null : Number(payout);
    if (payoutEarned != null && !Number.isFinite(payoutEarned)) throw new Error("Enter a valid payout amount.");
  } else {
    payoutEarned = computed;
  }
  const { data: { user } } = await supabase.auth.getUser();
  const row = {
    account_id: acct,
    program_id: programId,
    goal_id: goal.id,
    actual_value: actualValue,
    payout_earned: payoutEarned,
    // How this number was obtained (item 231). 'system' means the rep accepted
    // the figure the resolver pulled from the depletion reports unchanged;
    // anything typed or overridden is 'manual_upload'. Only the two enum values
    // are ever written — a caller can't invent a third.
    resolved_source: resolvedSource === DATA_SOURCE.SYSTEM ? DATA_SOURCE.SYSTEM : DATA_SOURCE.MANUAL,
    scored_by: user?.id ?? null,
    scored_at: new Date().toISOString(),
    notes: notes ? String(notes).slice(0, 2000) : null,
  };
  const { error } = await supabase
    .from("incentive_scores").upsert(row, { onConflict: "goal_id" });
  if (error) { console.error("[incentiveService.saveScore]", error); throw liftError(error, "Could not save the score."); }
  return payoutEarned;
}

// ── Copy-from-prior: deep-clone a program + its goals into a fresh draft ──────
/**
 * Deep-clone an existing program and its goals into a NEW draft. The copy resets
 * status to draft, clears review/sent/score state, and sets source_program_id to
 * the original. Scores and attachments are intentionally NOT copied.
 * // DECISION: dates are carried over verbatim — the rep almost always shifts
 * // them to the new period, and an empty range is worse than a wrong-but-visible
 * // one. The editor opens on the copy so they're adjusted before sending.
 */
export async function copyProgram(sourceId) {
  const acct = await activeAccountId();
  const { program, goals } = await getProgram(sourceId);

  const newName = `${program.name} (copy)`.slice(0, 160);
  const { data: created, error: pErr } = await supabase
    .from("incentive_programs")
    .insert({
      account_id: acct,
      name: newName,
      distributor_name: program.distributor_name,
      target_states: program.target_states ?? [],
      brand_manager_name: program.brand_manager_name,
      brand_manager_email: program.brand_manager_email,
      start_date: program.start_date,
      end_date: program.end_date,
      notes: program.notes,
      source_program_id: program.id,
    })
    .select("id").single();
  if (pErr) { console.error("[incentiveService.copyProgram.program]", pErr); throw liftError(pErr, "Could not copy the program."); }

  if (goals.length > 0) {
    const goalRows = goals.map((g) => ({
      account_id: acct,
      program_id: created.id,
      metric_type: g.metric_type,
      description: g.description,
      custom_text: g.custom_text,
      target_value: g.target_value,
      unit_label: g.unit_label,
      // Derived, exactly as on a fresh write — a copied goal that can resolve
      // automatically must not come back marked manual.
      data_source: hasAutomaticSource(g) ? DATA_SOURCE.SYSTEM : DATA_SOURCE.MANUAL,
      payout_type: g.payout_type,
      payout_amount: g.payout_amount,
      payout_tiers: g.payout_tiers ?? [],
      payout_custom: g.payout_custom,
      target_accounts: g.target_accounts ?? [],
      target_products: g.target_products ?? [],
      sort_order: g.sort_order,
    }));
    const { error: gErr } = await supabase.from("incentive_goals").insert(goalRows);
    if (gErr) {
      console.error("[incentiveService.copyProgram.goals]", gErr);
      // Roll back the half-made copy so we don't leave an orphan draft.
      await supabase.from("incentive_programs").delete().eq("account_id", acct).eq("id", created.id);
      throw liftError(gErr, "Could not copy the program's goals.");
    }
  }
  return created.id;
}

// ── Distributor progress (item 232) ──────────────────────────────────────────
// The FAQ answered "can distributors see their own progress?" with "yes" while
// no such surface existed anywhere in the product. This is the read side of it:
// every program a given distributor is running, with its goals and any captured
// scores, in three queries rather than one per program.
//
// Only programs that have actually been handed over are included — a draft or a
// program still in review is internal, and showing it under a distributor's name
// would leak an unagreed offer.
export const DISTRIBUTOR_VISIBLE_STATUSES = [STATUS.SENT, STATUS.ACTIVE, STATUS.SCORING, STATUS.COMPLETED];

/** Distributors with at least one handed-over program, with counts. */
export async function listDistributorsWithPrograms() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("incentive_programs")
    .select("distributor_name, status, start_date, end_date")
    .eq("account_id", acct)
    .in("status", DISTRIBUTOR_VISIBLE_STATUSES);
  if (error) { console.error("[incentiveService.listDistributorsWithPrograms]", error); throw new Error("Could not load distributors."); }

  const byName = new Map();
  for (const row of data ?? []) {
    const name = (row.distributor_name || "").trim();
    if (!name) continue;
    const entry = byName.get(name) || { distributor: name, total: 0, live: 0, completed: 0 };
    entry.total += 1;
    if (row.status === STATUS.COMPLETED) entry.completed += 1;
    else entry.live += 1;
    byName.set(name, entry);
  }
  return [...byName.values()].sort((a, b) => a.distributor.localeCompare(b.distributor));
}

/** Every handed-over program for one distributor, with goals + scores attached. */
export async function getDistributorPrograms(distributor) {
  const acct = await activeAccountId();
  const name = String(distributor || "").trim();
  if (!name) return [];

  const { data: programs, error } = await supabase
    .from("incentive_programs")
    .select("id, name, distributor_name, target_states, start_date, end_date, status, sent_at, brand_manager_name")
    .eq("account_id", acct)
    .eq("distributor_name", name)
    .in("status", DISTRIBUTOR_VISIBLE_STATUSES)
    .order("start_date", { ascending: false, nullsFirst: false });
  if (error) { console.error("[incentiveService.getDistributorPrograms]", error); throw new Error("Could not load this distributor's programs."); }
  if (!programs || programs.length === 0) return [];

  const ids = programs.map((p) => p.id);
  const [goalsRes, scoresRes] = await Promise.all([
    supabase.from("incentive_goals").select("*").eq("account_id", acct).in("program_id", ids).order("sort_order", { ascending: true }),
    supabase.from("incentive_scores").select("*").eq("account_id", acct).in("program_id", ids),
  ]);
  for (const r of [goalsRes, scoresRes]) {
    if (r.error) { console.error("[incentiveService.getDistributorPrograms]", r.error); throw new Error("Could not load this distributor's programs."); }
  }

  const goalsBy = new Map();
  for (const g of goalsRes.data ?? []) {
    if (!goalsBy.has(g.program_id)) goalsBy.set(g.program_id, []);
    goalsBy.get(g.program_id).push(g);
  }
  const scoresBy = new Map();
  for (const s of scoresRes.data ?? []) {
    if (!scoresBy.has(s.program_id)) scoresBy.set(s.program_id, []);
    scoresBy.get(s.program_id).push(s);
  }

  return programs.map((program) => ({
    program,
    goals: goalsBy.get(program.id) ?? [],
    scores: scoresBy.get(program.id) ?? [],
  }));
}

/** The active account id, for callers that need it (the resolver needs a tenant). */
export function getIncentiveAccountId() {
  return activeAccountId();
}
